import type { NavItemIdentifier } from 'src/types/nav-favorites';

import { useState } from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

import { useNavFavorites } from 'src/contexts/nav-favorites-context';

import { Iconify } from 'src/components/iconify';

import { navSectionClasses } from '../styles';

import type { NavItemDataProps } from '../types';

// ----------------------------------------------------------------------

type NavItemWithFavoriteProps = {
  children: React.ReactNode;
  item: NavItemDataProps;
  parentPath?: string;
  showFavoriteButton?: boolean;
};

export function NavItemWithFavorite({
  children,
  item,
  parentPath,
  showFavoriteButton = true,
}: NavItemWithFavoriteProps) {
  const { isItemFavorite, toggleFavorite } = useNavFavorites();
  const [isHovered, setIsHovered] = useState(false);

  const identifierParentPath = parentPath ?? item.favoriteParentPath;

  const navItemIdentifier: NavItemIdentifier = {
    path: item.path,
    title: item.title,
    parentPath: identifierParentPath,
  };

  const isFavorite = isItemFavorite(navItemIdentifier);
  const shouldShowFavorite = showFavoriteButton && (isHovered || isFavorite);

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    toggleFavorite(navItemIdentifier);
  };

  if (!showFavoriteButton) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        ...(item.children && {
          /**
           * Leave room for the favorite button when the nav item shows
           * the collapse arrow. This keeps the star aligned with the arrow.
           */
          [`& .${navSectionClasses.item.arrow}`]: {
            marginRight: '24px',
          },
        }),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box sx={{ flex: 1 }}>{children}</Box>

      {shouldShowFavorite && (
        <Tooltip title={isFavorite ? 'Quitar de favoritos' : 'Añadir a favoritos'} placement="left">
          <IconButton
            size="small"
            onClick={handleToggleFavorite}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: isFavorite ? 1 : 0.7,
              color: isFavorite ? 'warning.main' : 'text.secondary',
              '&:hover': {
                opacity: 1,
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify
              icon={isFavorite ? 'eva:star-fill' : 'eva:star-outline'}
              width={18}
            />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
